import type {
    DasboardTechnicalPerformance,
    DashboardAverageRating,
    DashboardKeyMetricsResponse,
    DashboardOperaionPerformanceResponse,
    DashboardOperationSummaryResponse,
    DashboardPartSuggestionMetricResponse,
    DashboardPartSuggestionResponse,
    DashboardPartSuggestionSummaryByCustomerResponse,
    DashboardProcessingMetricResponse,
    DashboardProcessingSummaryResponse,
    DashboardTaskRepeatMetricResponse,
    DashboardTaskRepeatResponse,
    DashboardTaskRepeatSummaryByDateResponse,
    DashboardTicketSummaryByCustomerResponse,
    DashboardViolationCustomerResponse,
} from '@/types/apps/dashboard.type';
import type {
    ApiResponse,
    PaginatedResult,
    QueryParams,
} from '@/types/common/api.type';
import type { BaseApiClientInterface } from '@/types/service/base-api-client.interface';

type taskRepeatResponse = ApiResponse<
    PaginatedResult<DashboardTaskRepeatResponse>
>;

type keyMetricsResponse = ApiResponse<
    PaginatedResult<DashboardKeyMetricsResponse>
>;

type violationCustomerResponse = ApiResponse<
    PaginatedResult<DashboardViolationCustomerResponse>
>;

type partSuggestionResponse = ApiResponse<
    PaginatedResult<DashboardPartSuggestionResponse>
>;

type technicalTaskRepeatResponse = ApiResponse<
    PaginatedResult<DashboardTaskRepeatResponse>
>;
type technicalPerformanceResponse = ApiResponse<
    PaginatedResult<DasboardTechnicalPerformance>
>;

type AverageRatingResponse = ApiResponse<DashboardAverageRating>;

type TaskRepeatMetricResponse = ApiResponse<DashboardTaskRepeatMetricResponse>;

type ProcessingMetricResponse = ApiResponse<DashboardProcessingMetricResponse>;

type ProcessingSummaryResponse = ApiResponse<
    PaginatedResult<DashboardProcessingSummaryResponse>
>;

type OperationSummaryResponse = ApiResponse<
    PaginatedResult<DashboardOperationSummaryResponse>
>;

type TicketSummaryByCustomerResponse = ApiResponse<
    PaginatedResult<DashboardTicketSummaryByCustomerResponse>
>;

type TaskRepeatSummaryByDateResponse =
    ApiResponse<DashboardTaskRepeatSummaryByDateResponse>;

type PartSuggestionMetricResponse =
    ApiResponse<DashboardPartSuggestionMetricResponse>;

type PartSuggestionSummaryByCustomerResponse = ApiResponse<
    PaginatedResult<DashboardPartSuggestionSummaryByCustomerResponse>
>;

type OperationPerformanceResponse = ApiResponse<
    PaginatedResult<DashboardOperaionPerformanceResponse>
>;

export class DashboardService {
    public constructor(private apiClient: BaseApiClientInterface) {}

    public endpoints = {
        keyMetrics: () => `/api/dashboard/key-metrics`,
        taskRepeat: () => `/api/dashboard/task-repeat`,
        violationCustomer: () => `/api/dashboard/violation-customer`,
        partSuggestion: () => 'api/dashboard/part-suggestion',
        technicalTaskRepeat: () => 'api/dashboard/technical-task-repeat',
        technicalPerformance: () => 'api/dashboard/technical-performance',
        averageRating: () => 'api/dashboard/average-rating',
        taskRepeatMetric: () => `/api/dashboard/task-repeat-metric`,
        processingMetric: () => `/api/dashboard/processing-metric`,
        processingSummary: () => `/api/dashboard/processing-summary`,
        operationSummary: () => `/api/dashboard/operation-summary`,
        ticketSummaryByCustomer: () =>
            `/api/dashboard/ticket-summary-by-customer`,
        taskRepeatSummaryByDate: () =>
            `/api/dashboard/task-repeat-summary-by-date`,
        partSuggestionMetric: () => `/api/dashboard/part-suggestion-metric`,
        partSuggestionSummaryByCustomer: () =>
            `/api/dashboard/part-suggestion-summary-by-customer`,
        operationPerformance: () => `/api/dashboard/operation-performance`,
    };

    public getKeyMetrics(params?: QueryParams): Promise<keyMetricsResponse> {
        return this.apiClient.fetchApi(this.endpoints.keyMetrics(), params);
    }
    public getTaskRepeat(params?: QueryParams): Promise<taskRepeatResponse> {
        return this.apiClient.fetchApi(this.endpoints.taskRepeat(), params);
    }
    public getTaskRepeatByCustomer(
        params?: QueryParams,
        filterObject?: 'Customer'
    ): Promise<taskRepeatResponse> {
        return this.apiClient.fetchApi(this.endpoints.taskRepeat(), params);
    }

    public getTaskRepeatByDevice(
        params?: QueryParams,
        filterObject?: 'Device'
    ): Promise<taskRepeatResponse> {
        return this.apiClient.fetchApi(this.endpoints.taskRepeat(), params);
    }

    public getViolationCustomer(
        params?: QueryParams
    ): Promise<violationCustomerResponse> {
        return this.apiClient.fetchApi(
            this.endpoints.violationCustomer(),
            params
        );
    }

    public getPartSuggestion(
        params?: QueryParams
    ): Promise<partSuggestionResponse> {
        return this.apiClient.fetchApi(this.endpoints.partSuggestion(), params);
    }
    public getTechnicalTaskRepeat(
        params?: QueryParams
    ): Promise<technicalTaskRepeatResponse> {
        return this.apiClient.fetchApi(
            this.endpoints.technicalTaskRepeat(),
            params
        );
    }

    public getTechnicalPerformance(
        params?: QueryParams
    ): Promise<technicalPerformanceResponse> {
        return this.apiClient.fetchApi(
            this.endpoints.technicalPerformance(),
            params
        );
    }

    public getAverageRating(
        params?: QueryParams
    ): Promise<AverageRatingResponse> {
        return this.apiClient.fetchApi(this.endpoints.averageRating(), params);
    }

    public getTaskRepeatMetric(
        params?: QueryParams
    ): Promise<TaskRepeatMetricResponse> {
        return this.apiClient.fetchApi(
            this.endpoints.taskRepeatMetric(),
            params
        );
    }

    public getProcessingMetric(
        params?: QueryParams
    ): Promise<ProcessingMetricResponse> {
        return this.apiClient.fetchApi(
            this.endpoints.processingMetric(),
            params
        );
    }

    public getProcessingSummary(
        params?: QueryParams
    ): Promise<ProcessingSummaryResponse> {
        return this.apiClient.fetchApi(
            this.endpoints.processingSummary(),
            params
        );
    }

    public getOperationSummary(
        params?: QueryParams
    ): Promise<OperationSummaryResponse> {
        return this.apiClient.fetchApi(
            this.endpoints.operationSummary(),
            params
        );
    }

    public getTicketSummaryByCustomer(
        params?: QueryParams
    ): Promise<TicketSummaryByCustomerResponse> {
        return this.apiClient.fetchApi(
            this.endpoints.ticketSummaryByCustomer(),
            params
        );
    }

    public getTaskRepeatSummaryByDate(
        params?: QueryParams
    ): Promise<TaskRepeatSummaryByDateResponse> {
        return this.apiClient.fetchApi(
            this.endpoints.taskRepeatSummaryByDate(),
            params
        );
    }

    public getPartSuggestionMetric(
        params?: QueryParams
    ): Promise<PartSuggestionMetricResponse> {
        return this.apiClient.fetchApi(
            this.endpoints.partSuggestionMetric(),
            params
        );
    }

    public getPartSuggestionSummaryByCustomer(
        params?: QueryParams
    ): Promise<PartSuggestionSummaryByCustomerResponse> {
        return this.apiClient.fetchApi(
            this.endpoints.partSuggestionSummaryByCustomer(),
            params
        );
    }

    public getOperationPerformance(
        params?: QueryParams
    ): Promise<OperationPerformanceResponse> {
        return this.apiClient.fetchApi(
            this.endpoints.operationPerformance(),
            params
        );
    }
}
